/**
 * CollectionsWorkspace.jsx — FM5C Staff Collections Workspace
 * Authorization: FM5_IMPLEMENTATION_PLAN.md v2 §11.5
 *
 * FREEZE GATES IMPLEMENTED IN THIS FILE:
 *
 * Gate 1 — Provenance Truth (INV-FM5-COLLECT-02):
 *   aging_provenance = AUTHORITATIVE → normal aging/defaulter presentation
 *   NOT_AVAILABLE    → "Due date provenance unverified" — no overdue/defaulter conclusions
 *   MIXED            → age only the authoritative portion; show provenance-unverified separately
 *   Defaulters tab: only shows AUTHORITATIVE or MIXED cases — NOT_AVAILABLE cases are excluded.
 *
 * Gate 2 — Collectible Debt Formula (INV-FM5-COLLECT-01):
 *   Case detail shows full breakdown:
 *     Total charges → Less payments → Less credits → Less sponsor → Less govt = Collectible
 *   sponsor_covered and govt_covered lines hidden if = 0.
 *
 * Gate 3 — Promise-to-Pay (INV-FM5-PTP-01):
 *   Promise recording is purely operational metadata.
 *   Promise amounts are NOT deducted from displayed collectible debt.
 *   UI clearly states this distinction.
 *
 * Gate 4 — No collections action on zero collectible debt.
 *   Case open endpoint will return 422 — UI surfaces this correctly.
 *
 * Tabs:
 *  1. Dashboard      — totals, aging bucket summary
 *  2. Work Queue     — MONITORING + OPEN + DEFAULTED cases
 *  3. Aging Report   — full aging table (INV-FM5-COLLECT-02 visible)
 *  4. Defaulters     — DEFAULTED cases (AUTHORITATIVE/MIXED provenance only)
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  RefreshCw, Plus, AlertTriangle, Shield, Clock, Check,
  XCircle, ChevronDown, ChevronRight, BarChart2, FileWarning,
  User, Calendar, Activity, Info, Banknote, Bell, Brain,
} from "lucide-react";
// FM7 BOUND-REOPEN-FM7-05: Send Reminder + Explain with AI capability gates
import { useIntegrationStatus } from "../hooks/useIntegrationStatus";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("academiaos_token");
  return { Authorization: `Bearer ${t}`, "Content-Type": "application/json" };
}

const fmt = (v) => {
  const n = parseFloat(v || 0);
  return isNaN(n) ? "₹0.00" : `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};

// ── Provenance Badge (Gate 1 core) ────────────────────────────────────────────
function ProvenanceBadge({ provenance }) {
  const configs = {
    AUTHORITATIVE: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Authoritative Dates" },
    NOT_AVAILABLE: { cls: "bg-amber-50 text-amber-700 border-amber-200",  label: "Provenance Unverified" },
    MIXED:         { cls: "bg-blue-50 text-blue-700 border-blue-200",    label: "Mixed Provenance" },
  };
  const c = configs[provenance] || { cls: "bg-slate-50 text-slate-500 border-slate-200", label: provenance };
  return <span className={`px-2 py-0.5 text-xs font-medium border rounded ${c.cls}`}>{c.label}</span>;
}

// ── Gate 1: NOT_AVAILABLE Disclaimer ─────────────────────────────────────────
function ProvenanceUnavailableNotice({ amount }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
      <FileWarning className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
      <div className="text-sm text-amber-800">
        <p className="font-semibold">Due Date Provenance Unverified (INV-FM5-COLLECT-02)</p>
        <p className="mt-0.5">
          This student's charges have synthesized migration surrogate due dates (fee_assessment_id IS NULL).
          Overdue status and delinquency classification cannot be determined.
          {amount > 0 && <> Balance <strong>{fmt(amount)}</strong> requires manual review before a collection case can be opened.</>}
        </p>
        <p className="mt-1 text-xs font-medium">
          This preserves the frozen Defaulter Count: NOT_CONNECTED governance (FM5_IMPLEMENTATION_PLAN.md INV-FM5-COLLECT-02).
        </p>
      </div>
    </div>
  );
}

// ── Gate 1: MIXED Provenance Notice ──────────────────────────────────────────
function MixedProvenanceNotice({ authAmount, surrogateAmount }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg">
      <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
      <div className="text-sm text-blue-800">
        <p className="font-semibold">Mixed Provenance (INV-FM5-COLLECT-02)</p>
        <p className="mt-0.5">Authoritative overdue: <strong>{fmt(authAmount)}</strong> (aged normally)</p>
        <p>Provenance-unverified: <strong>{fmt(surrogateAmount)}</strong> (excluded from aging — cannot be classified as overdue)</p>
      </div>
    </div>
  );
}

// ── Gate 2: Collectible Debt Breakdown ────────────────────────────────────────
function CollectibleBreakdown({ breakdown }) {
  if (!breakdown) return null;
  const { total_charges, student_payments, liability_credits, sponsor_covered, govt_covered, collectible_debt } = breakdown;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 font-mono text-xs space-y-1">
      <div className="font-semibold text-slate-600 text-xs uppercase tracking-wider mb-2">
        Collectible Debt Breakdown (INV-FM5-COLLECT-01)
      </div>
      <div className="flex justify-between text-slate-700">
        <span>Total assessed charges</span><span>{fmt(total_charges)}</span>
      </div>
      <div className="flex justify-between text-slate-500">
        <span>Less: student payments applied</span><span>-{fmt(student_payments)}</span>
      </div>
      <div className="flex justify-between text-slate-500">
        <span>Less: credits applied</span><span>-{fmt(liability_credits)}</span>
      </div>
      {parseFloat(sponsor_covered || 0) > 0 && (
        <div className="flex justify-between text-slate-400 italic">
          <span>Less: sponsor covered (FM4B — not collectible)</span><span>-{fmt(sponsor_covered)}</span>
        </div>
      )}
      {parseFloat(govt_covered || 0) > 0 && (
        <div className="flex justify-between text-slate-400 italic">
          <span>Less: govt scheme covered (FM4C — not collectible)</span><span>-{fmt(govt_covered)}</span>
        </div>
      )}
      <div className="border-t border-slate-300 mt-1 pt-1 flex justify-between font-bold text-slate-800">
        <span>Collectible student debt</span><span>{fmt(collectible_debt)}</span>
      </div>
    </div>
  );
}

// ── Open Case Modal ───────────────────────────────────────────────────────────
function OpenCaseModal({ institutionId, onClose, onDone }) {
  const [studentAccountId, setStudentAccountId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const open = async () => {
    if (!studentAccountId.trim()) { toast.error("Student Account ID is required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/collections/cases?institution_id=${institutionId}`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ student_account_id: studentAccountId, notes }),
      });
      const data = await res.json();
      if (!res.ok) {
        // INELIGIBLE responses (COLLECT-01 or COLLECT-02 gate fires) are not errors but governed responses
        const detail = typeof data.detail === "object" ? data.detail : { message: data.detail };
        if (detail.reason === "DUE_DATE_PROVENANCE_UNVERIFIED") {
          toast.warning(`Case not opened: ${detail.message}`);
        } else {
          throw new Error(detail.message || data.detail || "Cannot open case");
        }
        onClose(); return;
      }
      toast.success("Collection case opened");
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="open-case-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Open Collection Case</h3>
          <button onClick={onClose}><XCircle className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-500 bg-slate-50 rounded p-2 space-y-1">
            <p><strong>Gate 1:</strong> All collectible charges must have authoritative due dates (INV-FM5-COLLECT-02)</p>
            <p><strong>Gate 2:</strong> Collectible debt must be &gt; 0 (INV-FM5-COLLECT-01)</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Student Account ID *</label>
            <input data-testid="case-student-account-id"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={studentAccountId} onChange={e => setStudentAccountId(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optional)</label>
            <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">Cancel</button>
          <button data-testid="open-case-submit" onClick={open} disabled={saving}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">
            {saving ? "Opening…" : "Open Case"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Promise-to-Pay Modal ──────────────────────────────────────────────────────
function PromiseModal({ caseId, institutionId, onClose, onDone }) {
  const [form, setForm] = useState({ promised_amount: "", promise_date: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const record = async () => {
    if (!form.promised_amount || !form.promise_date) { toast.error("Amount and date required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/collections/cases/${caseId}/promises?institution_id=${institutionId}`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ ...form, promised_amount: parseFloat(form.promised_amount) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      toast.success(`Promise recorded. ${data.invariant_note}`);
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="promise-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Record Promise-to-Pay</h3>
          <button onClick={onClose}><XCircle className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          {/* Gate 3: PTP invariant notice */}
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            <Shield className="w-3 h-3 inline mr-1" />
            <strong>INV-FM5-PTP-01:</strong> A promise does NOT reduce collectible debt.
            The displayed liability remains unchanged. Separate FM3 payment recording is required.
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Promised Amount (INR) *</label>
            <input data-testid="promise-amount" type="number" min="0.01"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.promised_amount} onChange={e => setForm(f=>({...f, promised_amount:e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Promise Date *</label>
            <input data-testid="promise-date" type="date"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.promise_date} onChange={e => setForm(f=>({...f, promise_date:e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optional)</label>
            <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.notes} onChange={e => setForm(f=>({...f, notes:e.target.value}))} />
          </div>
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">Cancel</button>
          <button data-testid="promise-submit" onClick={record} disabled={saving}
            className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg disabled:opacity-50">
            {saving ? "Recording…" : "Record Promise"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Case Detail Panel ─────────────────────────────────────────────────────────
function CaseDetail({ caseItem, institutionId, canManage, onRefresh, notifyActive, aiActive, onSendReminder, onExplainAi }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [modal, setModal] = useState(null);
  const [actionForm, setActionForm] = useState({ action_type: "NOTE_ADDED", description: "" });

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/collections/cases/${caseItem.id}?institution_id=${institutionId}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setDetail(data);
    } catch {}
    finally { setLoading(false); }
  }, [caseItem.id, institutionId]);

  useEffect(() => { if (expanded) loadDetail(); }, [expanded, loadDetail]);

  const recordAction = async () => {
    try {
      const res = await fetch(`${API}/api/finance/fm5/collections/cases/${caseItem.id}/actions?institution_id=${institutionId}`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify(actionForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success("Action recorded"); loadDetail(); onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const caseTransition = async (endpoint, opts = {}) => {
    try {
      const res = await fetch(`${API}/api/finance/fm5/collections/cases/${caseItem.id}/${endpoint}?institution_id=${institutionId}`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify(opts),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Case ${endpoint}`); loadDetail(); onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const statusColors = {
    MONITORING: "bg-slate-50 text-slate-600 border-slate-200",
    OPEN:       "bg-blue-50 text-blue-700 border-blue-200",
    DEFAULTED:  "bg-red-50 text-red-700 border-red-200",
    SUSPENDED:  "bg-amber-50 text-amber-700 border-amber-200",
    RESOLVED:   "bg-emerald-50 text-emerald-700 border-emerald-200",
    CLOSED:     "bg-slate-100 text-slate-500 border-slate-200",
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`case-item-${caseItem.id}`}>
      {/* Header row */}
      <button onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 text-left">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`px-2 py-0.5 text-xs font-medium border rounded ${statusColors[caseItem.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
            {caseItem.status}
          </span>
          <span className="font-mono text-xs text-slate-500">{caseItem.student_account_id?.slice(0,12)}…</span>
          <ProvenanceBadge provenance={caseItem.aging_provenance} />
          {caseItem.aging_provenance !== "NOT_AVAILABLE" && (
            <span className="text-xs text-slate-600">
              Bucket: <strong>{caseItem.aging_bucket?.replace(/_/g," ")}</strong>
            </span>
          )}
          <span className="text-sm font-semibold text-slate-800">{fmt(caseItem.collectible_amount_at_open)}</span>
          {caseItem.hold_placed && (
            <span className="px-2 py-0.5 text-xs bg-rose-50 text-rose-700 border border-rose-200 rounded">Hold Active</span>
          )}
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4 space-y-4">
          {loading && <div className="text-slate-400 text-sm">Loading detail…</div>}

          {/* Gate 1: Provenance notices */}
          {caseItem.aging_provenance === "NOT_AVAILABLE" && (
            <ProvenanceUnavailableNotice amount={caseItem.provenance_unverified_amount} />
          )}
          {caseItem.aging_provenance === "MIXED" && detail && (
            <MixedProvenanceNotice
              authAmount={detail.live_collectible_breakdown?.collectible_debt}
              surrogateAmount={caseItem.provenance_unverified_amount}
            />
          )}

          {/* Gate 2: Collectible debt breakdown */}
          {detail?.live_collectible_breakdown && (
            <CollectibleBreakdown breakdown={detail.live_collectible_breakdown} />
          )}

          {/* Actions timeline */}
          {detail?.actions?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Action History</h4>
              <div className="space-y-1">
                {detail.actions.map(a => (
                  <div key={a.id} className="flex gap-2 text-xs text-slate-600">
                    <Activity className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />
                    <span className="font-medium text-slate-700">{a.action_type?.replace(/_/g," ")}</span>
                    <span className="text-slate-400">{a.action_date}</span>
                    {a.description && <span className="text-slate-500 truncate max-w-xs">{a.description}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gate 3: Promises-to-pay (with explicit INV-FM5-PTP-01 note) */}
          {detail?.promises?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                Promises-to-Pay
                <span className="ml-2 font-normal text-slate-400 normal-case">(INV-FM5-PTP-01: amounts do NOT reduce collectible debt)</span>
              </h4>
              <div className="space-y-1">
                {detail.promises.map(p => (
                  <div key={p.id} className="flex gap-3 text-xs text-slate-600 items-center">
                    <Calendar className="w-3 h-3 text-amber-500 shrink-0" />
                    <span>{fmt(p.promised_amount)} by {p.promise_date}</span>
                    <span className={`px-1.5 py-0.5 border rounded text-xs ${
                      p.status==="ACTIVE"?"bg-amber-50 text-amber-700 border-amber-200":
                      p.status==="KEPT"?"bg-emerald-50 text-emerald-700 border-emerald-200":
                      "bg-slate-50 text-slate-500 border-slate-200"
                    }`}>{p.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          {canManage && !["RESOLVED","CLOSED"].includes(caseItem.status) && (
            <div className="border-t border-slate-200 pt-3 space-y-3">
              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Record Action</h4>
              <div className="flex gap-2">
                <select className="border border-slate-200 rounded px-2 py-1 text-xs"
                  value={actionForm.action_type} onChange={e => setActionForm(f=>({...f, action_type:e.target.value}))}>
                  {["NOTE_ADDED","REMINDER_SENT","DEMAND_NOTICE_ISSUED","HOLD_PLACED","HOLD_RELEASED","SETTLEMENT_OFFERED"].map(t=>(
                    <option key={t} value={t}>{t.replace(/_/g," ")}</option>
                  ))}
                </select>
                <input className="flex-1 border border-slate-200 rounded px-2 py-1 text-xs"
                  placeholder="Description"
                  value={actionForm.description} onChange={e => setActionForm(f=>({...f, description:e.target.value}))} />
                <button onClick={recordAction} data-testid={`record-action-btn-${caseItem.id}`}
                  className="px-3 py-1 text-xs bg-slate-700 text-white rounded hover:bg-slate-800">Log</button>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setModal("promise")} data-testid={`promise-btn-${caseItem.id}`}
                  className="px-2 py-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100">
                  Record Promise
                </button>
                {/* FM7 BOUND-REOPEN-FM7-05: Send Reminder (NOTIFICATIONS ACTIVE only) */}
                {notifyActive ? (
                  <button
                    data-testid={`send-reminder-btn-${caseItem.id}`}
                    onClick={() => onSendReminder(caseItem.student_account_id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
                  >
                    <Bell className="w-3 h-3" /> Send Reminder
                  </button>
                ) : null}
                {/* FM7 BOUND-REOPEN-FM7-05: Explain with AI (AI_PROVIDERS ACTIVE only) */}
                {aiActive ? (
                  <button
                    data-testid={`explain-ai-btn-${caseItem.id}`}
                    onClick={() => onExplainAi(caseItem.student_account_id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                  >
                    <Brain className="w-3 h-3" /> Explain with AI
                  </button>
                ) : null}
                {caseItem.status === "OPEN" && (
                  <button onClick={() => caseTransition("escalate")} data-testid={`escalate-btn-${caseItem.id}`}
                    className="px-2 py-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100">
                    Escalate to Default
                  </button>
                )}
                {["OPEN","DEFAULTED"].includes(caseItem.status) && (
                  <button onClick={() => caseTransition("suspend", { notes: "Hardship deferral" })}
                    className="px-2 py-1 text-xs bg-slate-50 text-slate-600 border border-slate-200 rounded hover:bg-slate-100">
                    Suspend
                  </button>
                )}
                <button onClick={() => {
                  const rt = prompt("Resolution type: PAYMENT_RECEIVED | CREDIT_APPLIED | WRITTEN_OFF | HARDSHIP_DEFERRAL | ERROR_CORRECTION");
                  if (rt) caseTransition("resolve", { resolution_type: rt });
                }} data-testid={`resolve-btn-${caseItem.id}`}
                  className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100">
                  Resolve
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {modal === "promise" && (
        <PromiseModal caseId={caseItem.id} institutionId={institutionId} onClose={() => setModal(null)} onDone={loadDetail} />
      )}
    </div>
  );
}

// ── Dashboard Summary ─────────────────────────────────────────────────────────
function DashboardTab({ institutionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId) return;
    fetch(`${API}/api/finance/fm5/collections/dashboard?institution_id=${institutionId}`, { headers: authHeaders() })
      .then(r => r.json()).then(setData).catch(() => toast.error("Failed to load dashboard"))
      .finally(() => setLoading(false));
  }, [institutionId]);

  if (loading) return <div className="text-center py-12 text-slate-400">Loading…</div>;
  if (!data) return null;

  const bucketOrder = ["DAYS_91_PLUS","DAYS_61_90","DAYS_31_60","DAYS_1_30","CURRENT","NOT_AVAILABLE"];
  const bucketColors = {
    DAYS_91_PLUS: "bg-red-500", DAYS_61_90: "bg-orange-400",
    DAYS_31_60: "bg-amber-400", DAYS_1_30: "bg-yellow-300",
    CURRENT: "bg-emerald-400", NOT_AVAILABLE: "bg-slate-300",
  };

  return (
    <div className="space-y-5" data-testid="collections-dashboard">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500 mb-1">Active Cases</div>
          <div className="text-2xl font-bold text-slate-800">{data.total_active_cases}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-xs text-slate-500 mb-1">Total Overdue (Authoritative)</div>
          <div className="text-2xl font-bold text-slate-800">{fmt(data.total_overdue)}</div>
        </div>
      </div>

      {/* Aging bucket breakdown */}
      {data.by_bucket.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-700 text-sm">By Aging Bucket</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Bucket</th>
                <th className="px-4 py-2 text-left">Provenance</th>
                <th className="px-4 py-2 text-right">Cases</th>
                <th className="px-4 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.by_bucket.map((row, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${bucketColors[row.aging_bucket] || "bg-slate-300"}`} />
                      <span>{row.aging_bucket?.replace(/_/g," ")}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2"><ProvenanceBadge provenance={row.aging_provenance} /></td>
                  <td className="px-4 py-2 text-right">{row.case_count}</td>
                  <td className="px-4 py-2 text-right font-medium">{fmt(row.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-slate-400 bg-white border border-slate-200 rounded-xl">
          <BarChart2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No active collection cases</p>
        </div>
      )}
    </div>
  );
}

// ── Aging Report Tab (Gate 1 full display) ────────────────────────────────────
function AgingReportTab({ institutionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!institutionId) return;
    fetch(`${API}/api/finance/fm5/collections/aging?institution_id=${institutionId}`, { headers: authHeaders() })
      .then(r => r.json()).then(setData).catch(() => toast.error("Failed to load aging report"))
      .finally(() => setLoading(false));
  }, [institutionId]);

  if (loading) return <div className="text-center py-12 text-slate-400">Loading…</div>;
  if (!data) return null;

  const report = data.aging_report || [];
  const authoritative = report.filter(r => r.aging_provenance !== "NOT_AVAILABLE");
  const unverified = report.filter(r => r.aging_provenance === "NOT_AVAILABLE");

  return (
    <div className="space-y-4" data-testid="aging-report">
      {/* Gate 1: show INV-FM5-COLLECT-02 note prominently */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-800">
        <Shield className="w-3 h-3 inline mr-1" />
        {data.note}
      </div>

      {authoritative.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-700 text-sm">Authoritative Aging</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Student Account</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Bucket</th>
                <th className="px-4 py-2 text-left">Provenance</th>
                <th className="px-4 py-2 text-left">Overdue Since</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2 text-center">Hold</th>
              </tr>
            </thead>
            <tbody>
              {authoritative.map(row => (
                <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs">{row.student_id || row.student_account_id?.slice(0,12)}</td>
                  <td className="px-4 py-2"><span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">{row.status}</span></td>
                  <td className="px-4 py-2 text-xs font-medium">{row.aging_bucket?.replace(/_/g," ")}</td>
                  <td className="px-4 py-2"><ProvenanceBadge provenance={row.aging_provenance} /></td>
                  <td className="px-4 py-2 text-xs text-slate-500">{row.overdue_since || "—"}</td>
                  <td className="px-4 py-2 text-right font-medium">{fmt(row.collectible_amount_at_open)}</td>
                  <td className="px-4 py-2 text-center">{row.hold_placed ? "🔒" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Gate 1: NOT_AVAILABLE section — explicitly labelled, no overdue conclusion shown */}
      {unverified.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-amber-100 bg-amber-50">
            <h3 className="font-semibold text-amber-800 text-sm flex items-center gap-2">
              <FileWarning className="w-4 h-4" />
              Provenance-Unverified — No Overdue Classification
            </h3>
            <p className="text-xs text-amber-700 mt-0.5">
              These students have balances but SURROGATE due dates only. Overdue status cannot be determined.
              Manual review required. (INV-FM5-COLLECT-02 — Frozen Defaulter Count: NOT_CONNECTED)
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-amber-50 text-xs text-amber-700 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">Student Account</th>
                <th className="px-4 py-2 text-right">Balance (Unclassified)</th>
                <th className="px-4 py-2 text-center">Overdue?</th>
              </tr>
            </thead>
            <tbody>
              {unverified.map(row => (
                <tr key={row.id} className="border-t border-amber-100">
                  <td className="px-4 py-2 font-mono text-xs">{row.student_id || row.student_account_id?.slice(0,12)}</td>
                  <td className="px-4 py-2 text-right font-medium">{fmt(row.collectible_amount_at_open)}</td>
                  <td className="px-4 py-2 text-center">
                    <span className="text-xs text-amber-700">Cannot determine</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {report.length === 0 && (
        <div className="text-center py-8 text-slate-400 bg-white border border-slate-200 rounded-xl">
          No active collection cases in aging report
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CollectionsWorkspace() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOpenCase, setShowOpenCase] = useState(false);

  const institutionId = user?.institution_id;
  const role = user?.role;
  const canManage = ["finance_officer","finance_manager","cfo","institution_admin","super_admin"].includes(role);

  // FM7 BOUND-REOPEN-FM7-05: Send Reminder + Explain with AI capability gates
  // These capabilities are consumed here; configuration lives in /admin/integrations
  const { isActive: integActive, statusOf: integStatus } = useIntegrationStatus();
  const notifyActive = integActive("NOTIFICATIONS");
  const aiActive     = integActive("AI_PROVIDERS");

  // Send Reminder via FM7C notification
  const sendReminder = useCallback(async (studentAccountId) => {
    try {
      await fetch(`${API}/api/finance/notifications/send`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          event_type: "COLLECTIONS_REMINDER",
          channel: "IN_APP",
          recipient_id: studentAccountId,
          provider: "test_notify",
          context_ref: studentAccountId,
        }),
      });
      toast.success("Reminder sent via notification service (FM7C)");
    } catch { toast.error("Failed to send reminder"); }
  }, []);

  // Explain with AI via FM7D
  const [aiExplanation, setAiExplanation] = useState(null);
  const [aiLoading, setAiLoading]         = useState(false);
  const [aiForAccount, setAiForAccount]   = useState(null);

  const explainWithAi = useCallback(async (studentAccountId) => {
    setAiLoading(true);
    setAiForAccount(studentAccountId);
    setAiExplanation(null);
    try {
      const token = localStorage.getItem("academiaos_token");
      const resp = await fetch(`${API}/api/finance/ai/explain`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ student_account_id: studentAccountId }),
      });
      if (!resp.ok) { const d = await resp.json(); throw new Error(d.detail || "AI failed"); }
      // SSE streaming — collect chunks
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let text = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        // SSE data lines
        chunk.split("\n").forEach(line => {
          if (line.startsWith("data: ")) {
            try {
              const d = JSON.parse(line.slice(6));
              if (d.type === "text_delta") text += d.text;
              if (d.type === "message_complete") text = d.full_text || text;
            } catch {}
          }
        });
        setAiExplanation(text || "Processing…");
      }
      if (!text) text = "AI explanation complete.";
      setAiExplanation(text);
    } catch (e) { toast.error(e.message || "AI explanation failed"); }
    finally { setAiLoading(false); }
  }, []);

  const loadCases = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/collections/cases?institution_id=${institutionId}`, { headers: authHeaders() });
      const data = await res.json();
      setCases(Array.isArray(data) ? data : []);
    } catch { toast.error("Failed to load cases"); }
    finally { setLoading(false); }
  }, [institutionId]);

  useEffect(() => {
    if (tab !== "dashboard" && tab !== "aging") loadCases();
  }, [tab, loadCases]);

  // Gate 1: Defaulters tab shows ONLY AUTHORITATIVE or MIXED — NOT_AVAILABLE excluded
  const defaulterCases = cases.filter(c =>
    c.status === "DEFAULTED" && c.aging_provenance !== "NOT_AVAILABLE"
  );
  const workQueueCases = cases.filter(c =>
    ["MONITORING","OPEN","DEFAULTED"].includes(c.status)
  );

  const tabs = [
    { id: "dashboard", label: "Dashboard" },
    { id: "queue",     label: "Work Queue", count: workQueueCases.length },
    { id: "aging",     label: "Aging Report" },
    { id: "defaulters",label: "Defaulters", count: defaulterCases.length },
  ];

  return (
    <div className="p-6 space-y-5" data-testid="collections-workspace">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Collections</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Collectible student debt only. Sponsor and government receivables excluded.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadCases} className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          {canManage && (
            <button onClick={() => setShowOpenCase(true)} data-testid="open-case-btn"
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" /> Open Case
            </button>
          )}
        </div>
      </div>

      {/* FM7 BOUND-REOPEN-FM7-05: Capability surfaces for Collections
          Send Reminder — enabled only when NOTIFICATIONS ACTIVE
          Explain with AI — enabled only when AI_PROVIDERS ACTIVE
          CONFIGURED ≠ ACTIVE (INV-FM7-FOUND-01) */}
      {canManage && (
        <div className="flex flex-wrap gap-2" data-testid="fm7-collections-capabilities">
          {/* Send Reminder */}
          {notifyActive ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg">
              <Bell className="w-3.5 h-3.5" />
              <span>Send Reminder available — select a case to use</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-lg"
              data-testid="send-reminder-not-available">
              <Bell className="w-3.5 h-3.5 opacity-40" />
              <span>
                {integStatus("NOTIFICATIONS") === "CONFIGURED"
                  ? "Notifications configured but not active"
                  : "Send Reminder — no notification provider configured"}
              </span>
            </div>
          )}
          {/* Explain with AI */}
          {aiActive ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg">
              <Brain className="w-3.5 h-3.5" />
              <span>AI explanation available — select a case to use</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-lg"
              data-testid="explain-ai-not-available">
              <Brain className="w-3.5 h-3.5 opacity-40" />
              <span>
                {integStatus("AI_PROVIDERS") === "CONFIGURED"
                  ? "AI provider configured but not active"
                  : "Explain with AI — no AI provider configured"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* FM7 AI explanation panel */}
      {aiExplanation && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="ai-explanation-panel">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-emerald-800 flex items-center gap-1.5">
              <Brain className="w-3.5 h-3.5" />
              AI Finance Explanation (Advisory Only — not a canonical financial authority)
            </span>
            <button onClick={() => setAiExplanation(null)} className="text-emerald-600 hover:text-emerald-800">
              <XCircle className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
            {aiLoading ? "Generating…" : aiExplanation}
          </div>
        </div>
      )}

      <div className="flex border-b border-slate-200 gap-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`collections-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? "border-blue-500 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            {t.label}
            {t.count > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "dashboard" && <DashboardTab institutionId={institutionId} />}

      {tab === "aging" && <AgingReportTab institutionId={institutionId} />}

      {tab === "queue" && (
        <div className="space-y-3" data-testid="work-queue">
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading…</div>
          ) : workQueueCases.length === 0 ? (
            <div className="text-center py-12 text-slate-400">No active collection cases</div>
          ) : (
            workQueueCases.map(c => (
              <CaseDetail key={c.id} caseItem={c} institutionId={institutionId} canManage={canManage} onRefresh={loadCases}
                notifyActive={notifyActive} aiActive={aiActive} onSendReminder={sendReminder} onExplainAi={explainWithAi} />
            ))
          )}
        </div>
      )}

      {tab === "defaulters" && (
        <div className="space-y-3" data-testid="defaulters-tab">
          {/* Gate 1: Explicit label that NOT_AVAILABLE cases are excluded */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-800">
            <Shield className="w-3 h-3 inline mr-1" />
            <strong>Gate 1 (INV-FM5-COLLECT-02):</strong> Only cases with AUTHORITATIVE or MIXED provenance shown here.
            Cases with SURROGATE (NOT_AVAILABLE) due dates are excluded from defaulter classification —
            they cannot be aged or declared as defaulters.
          </div>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading…</div>
          ) : defaulterCases.length === 0 ? (
            <div className="text-center py-8 text-slate-400">No defaulted cases with authoritative aging data</div>
          ) : (
            defaulterCases.map(c => (
              <CaseDetail key={c.id} caseItem={c} institutionId={institutionId} canManage={canManage} onRefresh={loadCases}
                notifyActive={notifyActive} aiActive={aiActive} onSendReminder={sendReminder} onExplainAi={explainWithAi} />
            ))
          )}
        </div>
      )}

      {showOpenCase && (
        <OpenCaseModal institutionId={institutionId} onClose={() => setShowOpenCase(false)} onDone={loadCases} />
      )}
    </div>
  );
}
